import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Group, Paper, Stack, Text, TextInput, Title } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';

import { BasicButton } from '@components/Button';
import {
  CollectionCard,
  ManagementLayout,
  PageTitle,
  PaginatedList,
  SearchInput,
} from '@components/shared';
import entityColor from '@util/entityColor';
import entityIcon from '@util/entityIcon';
import { toErrorMessage } from '@util/toErrorMessage';

function AthleteCreateForm({ onCreated, onCancel }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [sport, setSport] = useState('');
  const [position, setPosition] = useState('');
  const [saving, setSaving] = useState(false);

  function onSubmit(event) {
    event.preventDefault();
    setSaving(true);
    const body = {
      first_name: firstName,
      last_name: lastName,
      email,
      sport: sport || null,
      position: position || null,
    };
    if (password) {
      body.password = password;
    }
    axios
      .post('/athletes/', body)
      .then(({ data }) => {
        const athlete = data.response?.[0];
        toast.success('Athlete created');
        onCreated(athlete);
      })
      .catch((error) => toast.error(toErrorMessage(error)))
      .finally(() => setSaving(false));
  }

  return (
    <Paper p="md" withBorder radius="md" component="form" onSubmit={onSubmit}>
      <Title order={4} mb="sm">
        New athlete
      </Title>
      <Stack gap="sm">
        <TextInput
          label="First name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          required
        />
        <TextInput
          label="Last name"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          required
        />
        <TextInput
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <TextInput
          label="Password"
          type="password"
          description="Optional — a random password is set if left blank"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <TextInput
          label="Sport"
          value={sport}
          onChange={(e) => setSport(e.target.value)}
        />
        <TextInput
          label="Position"
          value={position}
          onChange={(e) => setPosition(e.target.value)}
        />
        <Group>
          <BasicButton
            text="Create athlete"
            type="submit"
            loading={saving}
            disabled={saving}
            color="var(--brand-brick)"
            variant="filled"
            style={{
              background: 'var(--brand-brick)',
              borderColor: 'var(--brand-brick)',
            }}
            primary
          />
          <BasicButton
            text="Cancel"
            type="button"
            onClick={onCancel}
            disabled={saving}
            color="gray"
            variant="outline"
          />
        </Group>
      </Stack>
    </Paper>
  );
}

export default function AdminAthletes() {
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [listKey, setListKey] = useState(0);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  function onCreated(athlete) {
    setCreating(false);
    setListKey((key) => key + 1);
    queryClient.invalidateQueries({ queryKey: ['paginated-list'] });
    if (athlete?.id) {
      navigate(`/athletes/${athlete.id}`);
    }
  }

  return (
    <div>
      <PageTitle
        title="Athletes"
        subtitle="Browse athletes and today’s training"
        icon={entityIcon.athlete()}
        iconColor={entityColor.athlete}
        actions={
          !creating ? (
            <BasicButton
              text="Add athlete"
              onClick={() => setCreating(true)}
              color={entityColor.athlete}
              variant="light"
              leftSection={entityIcon.athlete()}
            />
          ) : null
        }
      />
      <ManagementLayout
        createComponent={
          creating ? (
            <AthleteCreateForm
              onCreated={onCreated}
              onCancel={() => setCreating(false)}
            />
          ) : null
        }
        dataComponent={
          <div>
            <SearchInput
              value={name}
              onChange={setName}
              placeholder="Search by name..."
            />
            <PaginatedList
              key={listKey}
              requestInfo={{
                url: '/retrieve-athletes/',
                data: { name },
              }}
              displayFormatter={(item) => {
                const athlete = item.original;
                return (
                  <CollectionCard
                    key={athlete.id}
                    to={`/athletes/${athlete.id}`}
                    title={athlete.full_name}
                    meta={
                      <Text size="sm" c="dimmed">
                        {athlete.email}
                      </Text>
                    }
                    badge={athlete.status_formatted}
                    badgeColor={
                      athlete.status_formatted === 'active' ? 'teal' : 'gray'
                    }
                  />
                );
              }}
            />
          </div>
        }
      />
    </div>
  );
}
